import React, { useEffect, useState, useCallback } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import Layout from "@/components/AdminLayout";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Download, Reply, MessageSquare, Search } from "lucide-react";

const STATUSES = [
  { key: "all", label: "All" },
  { key: "new", label: "New" },
  { key: "in_progress", label: "In Progress" },
  { key: "quoted", label: "Quoted" },
  { key: "completed", label: "Completed" },
  { key: "closed", label: "Closed" },
];

const STATUS_COLORS = {
  new: "bg-accent text-accent-foreground",
  in_progress: "bg-primary text-primary-foreground",
  quoted: "bg-blue-600 text-white",
  completed: "bg-green-700 text-white",
  closed: "bg-muted text-muted-foreground",
};

const Enquiries = () => {
  const { user, authLoading } = useApp();
  const [searchParams] = useSearchParams();
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [status, setStatus] = useState("all");
  const [type, setType] = useState(searchParams.get("type") || "all");
  const [q, setQ] = useState("");
  const [settings, setSettings] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const canAccess = user && ["super_admin", "admin", "sales"].includes(user.role);

  // Keep local filter in sync with URL (?type=contact|custom_design|quote)
  useEffect(() => {
    setType(searchParams.get("type") || "all");
  }, [searchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status !== "all") params.status = status;
      if (type !== "all") params.type = type;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/admin/enquiries", { params });
      setEnquiries(data);
    } catch (err) {
      // [removed console.debug]
      setEnquiries([]);
    } finally {
      setLoading(false);
    }
  }, [status, type, q]);

  useEffect(() => {
    document.title = "Enquiries — Dream Home Plans";
    if (canAccess) {
      load();
      api.get("/admin/settings").then((r) => setSettings(r.data)).catch(() => {});
    }
  }, [canAccess, load]);

  if (authLoading) return <Layout><div className="p-24 text-center">Loading…</div></Layout>;
  if (!user) return <Navigate to="/login" replace />;
  if (!canAccess) return <Layout><div className="p-24 text-center text-muted-foreground">Access denied.</div></Layout>;

  const exportCsv = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/enquiries/export.csv`;
    // Include Authorization via fetch → blob → download to preserve auth
    fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem("dh_token")}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        const objUrl = URL.createObjectURL(blob);
        a.href = objUrl;
        a.download = `enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(objUrl);
        toast.success("Exported");
      })
      .catch(() => toast.error("Export failed"));
  };

  return (
    <Layout title="Enquiries">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="overline text-accent">Administration</div>
            <p className="mt-2 text-muted-foreground text-sm">All contact form &amp; custom design requests.</p>
          </div>
          <div className="flex gap-2">
            {user.role === "super_admin" || user.role === "admin" ? (
              <Button variant="outline" onClick={() => setSettingsOpen(true)} className="rounded-none" data-testid="settings-btn">Notification settings</Button>
            ) : null}
            <Button onClick={exportCsv} className="rounded-none bg-primary" data-testid="export-btn">
              <Download size={14} className="mr-2" /> Export CSV
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="border border-border bg-muted/30 p-4 flex flex-wrap gap-3 items-center mb-6">
          <div className="relative flex-1 min-w-[240px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search name, email, subject, message…" value={q} onChange={(e) => setQ(e.target.value)} className="rounded-none pl-10 h-11" data-testid="enq-search" />
          </div>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="rounded-none h-11 w-40" data-testid="enq-filter-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="contact">Contact</SelectItem>
              <SelectItem value="custom_design">Custom Design</SelectItem>
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="rounded-none h-11 w-40" data-testid="enq-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="border border-border" data-testid="enquiries-table">
          <table className="w-full text-sm">
            <thead className="bg-muted/60">
              <tr className="text-left">
                <th className="px-4 py-3 overline">Received</th>
                <th className="px-4 py-3 overline">Type</th>
                <th className="px-4 py-3 overline">From</th>
                <th className="px-4 py-3 overline">Subject</th>
                <th className="px-4 py-3 overline">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={5} className="text-center py-12 text-muted-foreground">Loading…</td></tr>}
              {!loading && enquiries.map((e) => (
                <tr key={e.id} onClick={() => setSelected(e)} className="border-t border-border cursor-pointer hover:bg-muted/40" data-testid={`enq-row-${e.id}`}>
                  <td className="px-4 py-3 text-muted-foreground text-xs">{new Date(e.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3"><span className="text-xs bg-muted px-2 py-0.5">{e.type === "custom_design" ? "Custom" : "Contact"}</span></td>
                  <td className="px-4 py-3">
                    <div>{e.name}</div>
                    <div className="text-xs text-muted-foreground">{e.email}</div>
                  </td>
                  <td className="px-4 py-3 max-w-md truncate">{e.subject || e.message.slice(0, 60)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[e.status] || "bg-muted"}`}>
                      {STATUSES.find((s) => s.key === e.status)?.label || e.status}
                    </span>
                  </td>
                </tr>
              ))}
              {!loading && enquiries.length === 0 && (
                <tr><td colSpan={5} className="text-center py-12 text-muted-foreground">No enquiries.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <EnquiryDialog enquiry={selected} onClose={() => setSelected(null)} onSaved={load} user={user} />
      <SettingsDialog open={settingsOpen} settings={settings} onClose={() => setSettingsOpen(false)} onSaved={(s) => setSettings(s)} />
    </Layout>
  );
};

const EnquiryDialog = ({ enquiry, onClose, onSaved, user }) => {
  const [note, setNote] = useState("");
  const [replySubject, setReplySubject] = useState("");
  const [replyBody, setReplyBody] = useState("");
  const [replying, setReplying] = useState(false);
  const canReply = ["super_admin", "admin", "sales"].includes(user.role);

  useEffect(() => {
    if (enquiry) {
      setReplySubject(`Re: ${enquiry.subject || "your enquiry"}`);
      setReplyBody("");
      setNote("");
    }
  }, [enquiry]);

  if (!enquiry) return null;

  const changeStatus = async (newStatus) => {
    try {
      await api.patch(`/admin/enquiries/${enquiry.id}`, { status: newStatus });
      toast.success(`Status → ${newStatus}`);
      onSaved();
    } catch (e) { toast.error("Failed", { description: e.response?.data?.detail || "" }); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await api.post(`/admin/enquiries/${enquiry.id}/notes`, { note });
      setNote(""); onSaved();
      toast.success("Note added");
    } catch (e) { toast.error("Failed"); }
  };

  const sendReply = async () => {
    if (!replyBody.trim() || !replySubject.trim()) return;
    setReplying(true);
    try {
      await api.post(`/admin/enquiries/${enquiry.id}/reply`, { subject: replySubject, body: replyBody });
      toast.success("Reply sent");
      setReplyBody("");
      onSaved();
    } catch (e) { toast.error("Failed", { description: e.response?.data?.detail || "" }); }
    finally { setReplying(false); }
  };

  const convertToProject = async () => {
    if (!window.confirm(`Convert this enquiry from ${enquiry.name} into a new project?`)) return;
    try {
      const { data } = await api.post(`/admin/enquiries/${enquiry.id}/convert-to-project`);
      toast.success("Project created", { description: `Number: ${data.project.project_number}` });
      onSaved();
      window.location.href = `/admin/projects/${data.project.id}`;
    } catch (e) {
      toast.error("Convert failed", { description: e.response?.data?.detail || "" });
    }
  };

  return (
    <Dialog open={!!enquiry} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-none">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl leading-tight">{enquiry.subject || `${enquiry.type === "custom_design" ? "Custom Design" : "Contact"} enquiry`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-6 mt-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><div className="overline text-muted-foreground">From</div><div>{enquiry.name}</div><div className="text-xs text-muted-foreground">{enquiry.email}</div></div>
            <div><div className="overline text-muted-foreground">Received</div><div>{new Date(enquiry.created_at).toLocaleString()}</div></div>
            <div>
              <div className="overline text-muted-foreground">Status</div>
              <Select value={enquiry.status} onValueChange={changeStatus}>
                <SelectTrigger className="rounded-none mt-1 h-9" data-testid="enq-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.slice(1).map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><div className="overline text-muted-foreground">IP</div><div className="font-mono text-xs">{enquiry.ip || "—"}</div></div>
          </div>

          <div>
            <div className="overline text-muted-foreground mb-2">Message</div>
            <div className="p-4 border border-border bg-muted/40 whitespace-pre-line text-sm">{enquiry.message}</div>
          </div>

          {enquiry.form_data && Object.keys(enquiry.form_data).length > 0 && (
            <div>
              <div className="overline text-muted-foreground mb-2">Form details</div>
              <div className="border border-border">
                {Object.entries(enquiry.form_data).filter(([, v]) => v).map(([k, v]) => (
                  <div key={k} className="flex text-sm border-b border-border last:border-0">
                    <div className="w-40 shrink-0 px-3 py-2 bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">{k.replace(/_/g, " ")}</div>
                    <div className="flex-1 px-3 py-2">{v}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {enquiry.replies?.length > 0 && (
            <div>
              <div className="overline text-muted-foreground mb-2">Replies sent</div>
              {enquiry.replies.map((r) => (
                <div key={r.reply_id} className="border-l-4 border-accent pl-4 py-2 mb-3 text-sm">
                  <div className="text-xs text-muted-foreground">{new Date(r.timestamp).toLocaleString()} · by {r.sent_by_name} · {r.email_status}</div>
                  <div className="font-medium">{r.subject}</div>
                  <div className="whitespace-pre-line text-muted-foreground">{r.body}</div>
                </div>
              ))}
            </div>
          )}

          {enquiry.notes?.length > 0 && (
            <div>
              <div className="overline text-muted-foreground mb-2">Internal notes</div>
              {enquiry.notes.map((n) => (
                <div key={n.note_id} className="text-sm border-l-2 border-border pl-3 py-1 mb-2">
                  <div className="text-xs text-muted-foreground">{new Date(n.timestamp).toLocaleString()} · {n.user_name}</div>
                  <div className="whitespace-pre-line">{n.note}</div>
                </div>
              ))}
            </div>
          )}

          <div>
            <div className="overline text-muted-foreground mb-2 flex items-center gap-2"><MessageSquare size={12} /> Add internal note</div>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="rounded-none" placeholder="Only visible to admin/sales…" data-testid="enq-note-input" />
            <Button size="sm" onClick={addNote} disabled={!note.trim()} className="rounded-none mt-2 bg-primary" data-testid="enq-add-note">Add note</Button>
          </div>

          {canReply && (
            <div className="border-t border-border pt-6">
              <div className="overline text-accent mb-3 flex items-center gap-2"><Reply size={12} /> Reply to customer</div>
              <Input value={replySubject} onChange={(e) => setReplySubject(e.target.value)} placeholder="Subject" className="rounded-none h-11 mb-3" data-testid="reply-subject" />
              <Textarea rows={6} value={replyBody} onChange={(e) => setReplyBody(e.target.value)} placeholder={`Hi ${enquiry.name}, thanks for reaching out…`} className="rounded-none" data-testid="reply-body" />
              <Button onClick={sendReply} disabled={replying || !replyBody.trim()} data-testid="reply-send" className="rounded-none bg-accent hover:bg-accent/90 mt-3">
                {replying ? "Sending…" : "Send reply"}
              </Button>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            data-testid="convert-to-project-btn"
            onClick={convertToProject}
            className="rounded-none bg-accent hover:bg-accent/90 mr-auto"
            disabled={enquiry.status === "converted"}
          >
            {enquiry.status === "converted" ? "Already converted" : "Convert → Project"}
          </Button>
          <Button variant="outline" onClick={onClose} className="rounded-none">Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const SettingsDialog = ({ open, settings, onClose, onSaved }) => {
  const [form, setForm] = useState({ notification_email_primary: "", notification_email_cc: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings) setForm({
      notification_email_primary: settings.notification_email_primary || "",
      notification_email_cc: (settings.notification_email_cc || []).join(", "),
    });
  }, [settings]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const cc = form.notification_email_cc.split(",").map((s) => s.trim()).filter(Boolean);
      const { data } = await api.put("/admin/settings", {
        notification_email_primary: form.notification_email_primary,
        notification_email_cc: cc,
      });
      toast.success("Settings saved");
      onSaved(data);
      onClose();
    } catch (e) {
      toast.error("Failed", { description: e.response?.data?.detail || "" });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg rounded-none">
        <DialogHeader><DialogTitle className="font-display text-3xl">Notification settings</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-4">
          <div>
            <label className="overline text-muted-foreground">Primary notification email</label>
            <Input required type="email" value={form.notification_email_primary} onChange={(e) => setForm({ ...form, notification_email_primary: e.target.value })} className="rounded-none mt-2 h-12" data-testid="settings-primary" />
          </div>
          <div>
            <label className="overline text-muted-foreground">CC (comma-separated)</label>
            <Input value={form.notification_email_cc} onChange={(e) => setForm({ ...form, notification_email_cc: e.target.value })} placeholder="bchsrnag@gmail.com, another@example.com" className="rounded-none mt-2 h-12" data-testid="settings-cc" />
          </div>
          <p className="text-xs text-muted-foreground">Every new enquiry sends a notification email to the primary address and CCs each listed address. Customer also receives an automatic acknowledgement.</p>
          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-none">Cancel</Button>
            <Button type="submit" disabled={saving} className="rounded-none bg-accent hover:bg-accent/90" data-testid="settings-save">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default Enquiries;
